// screens/ComplaintReportsScreen.js - Reports: factory report, signal list and signal settings.
// This is where the Reports tile and tab land; the open-complaints overview is one step on.
// The factory report runs on the production date (photo date) so a complaint traces back
// to a production moment; lines still pending a verdict are included but marked, so no
// causer is implied before it is determined. Site users see only their own factory
// (the server scopes it; the factory picker is hidden for them).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from './HomeScreen';
import { SyncBar, EmptyState, SectionHeader, useSmartBack } from '../components/shared';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import api, { getErrorMessage } from '../utils/api';
import { deliverFile, filenameFromHeaders, isNativeShell, openExternalUrl } from '../utils/download';
import useComplaintTaxonomy from '../hooks/useComplaintTaxonomy';
import {
  STATUS_BADGE, canManageComplaintSettings, defaultComplaintFilters, formatDay, mainComplaintLabel,
} from '../utils/complaints';

const GROUPS = ['model', 'detail', 'month'];
// Long enough for a big PDF, short enough that a stuck request ends in a message
// instead of a button that stays on "Preparing..." for minutes.
const EXPORT_TIMEOUT_MS = 90000;

export default function ComplaintReportsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const back = useSmartBack('/');
  const isAdmin = canManageComplaintSettings(user);
  const tabs = ['factory', 'signals', ...(isAdmin ? ['settings'] : [])];
  const [tab, setTab] = useState('factory');

  return (
    <div className="screen">
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={back} aria-label={t('complaints.back')}>←</button>
        <h2 style={{ flex: 1, textAlign: 'center' }}>{t('complaints.reports')}</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/complaints/list')}>
          {t('complaints.openoverview')}
        </button>
      </div>
      <div style={{ padding: '0 16px' }}>
        <div className="settings-tabs" role="tablist">
          {tabs.map((k) => (
            <div key={k} role="tab" tabIndex={0} aria-selected={tab === k}
              className={`settings-tab ${tab === k ? 'active' : ''}`}
              onClick={() => setTab(k)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTab(k); }}>
              {t(`complaints.tab.${k}`)}
            </div>
          ))}
        </div>
      </div>
      <div className="page-content" style={{ paddingTop: 16 }}>
        {tab === 'factory' && <FactoryReport user={user} t={t} />}
        {tab === 'signals' && <SignalList t={t} />}
        {tab === 'settings' && isAdmin && <SignalSettings t={t} />}
      </div>
      <BottomNav active="complaints" />
    </div>
  );
}

function FactoryReport({ user, t }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { taxonomy } = useComplaintTaxonomy();
  const [sites, setSites] = useState([]);
  const [factory, setFactory] = useState('');
  const [period, setPeriod] = useState(() => {
    const d = defaultComplaintFilters();
    return { from: d.from, to: d.to, all: false };
  });
  const [group, setGroup] = useState('model');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportBusy = useRef(false);

  useEffect(() => {
    if (user?.site_id) return;
    api.get('/api/sites').then(({ data }) => setSites(data || [])).catch(() => {});
  }, [user?.site_id]);

  const params = useCallback((extra = {}) => {
    const p = { group, ...extra };
    if (factory) p.factory = factory;
    // Without all=1 the server falls back to its 30-day default, so "All" must say so.
    if (period.all) p.all = 1;
    else { p.from = period.from; p.to = period.to; }
    return p;
  }, [group, factory, period]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.get('/api/complaints/reports/factory', { params: params() })
      .then(({ data }) => { if (alive) setReport(data); })
      .catch((err) => { if (alive) setError(getErrorMessage(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [params]);

  // format: 'pdf' (report) or 'csv' (the same lines as columns, for Excel).
  // Whatever happens, the buttons come back: a failure ends in a message, never in a
  // button stuck on "Preparing...".
  const exportFile = async (format) => {
    if (exportBusy.current) return;
    exportBusy.current = true;
    setExporting(format);
    try {
      // Same two routes as the order export: the Android shell cannot receive a blob,
      // so it asks for a link and hands that to the system browser (utils/download.js).
      if (isNativeShell()) {
        const { data } = await api.get('/api/complaints/reports/factory', { params: params({ format, link: 1 }), timeout: EXPORT_TIMEOUT_MS });
        if (!data?.url) throw new Error('no download url');
        openExternalUrl(data.url);
        toast.success(t('complaints.pdf.browser'));
        return;
      }
      const res = await api.get('/api/complaints/reports/factory', { params: params({ format }), responseType: 'blob', timeout: EXPORT_TIMEOUT_MS });
      const ownName = `reports_${(factory || report?.factory || 'all').replace(/[^\w.-]+/g, '-')}_${period.all ? 'all' : `${period.from}_${period.to}`}.${format}`;
      const outcome = await deliverFile(res.data, filenameFromHeaders(res.headers, ownName));
      if (outcome === 'dismissed') return;
      if (outcome === 'unsupported') { toast.error(t('complaints.pdf.unsupported')); return; }
      toast.success(t('complaints.pdf.ready'));
    } catch (err) {
      toast.error(err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' ? t('complaints.pdf.timeout') : t('complaints.pdf.failed'));
    } finally {
      exportBusy.current = false;
      setExporting(false);
    }
  };

  const rows = report?.rows || [];
  const lines = report?.lines || [];
  const totals = rows.reduce((a, r) => ({
    total: a.total + (r.total || 0), solved: a.solved + (r.solved || 0),
    pending: a.pending + (r.pending || 0), unfounded: a.unfounded + (r.unfounded || 0),
  }), { total: 0, solved: 0, pending: 0, unfounded: 0 });

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          {!user?.site_id && (
            <select value={factory} onChange={(e) => setFactory(e.target.value)} className="input-field"
              aria-label={t('complaints.factory')} style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }}>
              <option value="">{t('complaints.allfactories')}</option>
              {sites.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          )}
          <input type="date" value={period.from} max={period.to} disabled={period.all} aria-label={t('complaints.from')}
            onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
            className="input-field" style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }} />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{t('complaints.to')}</span>
          <input type="date" value={period.to} min={period.from} disabled={period.all} aria-label={t('complaints.to')}
            onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
            className="input-field" style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }} />
          <button type="button" className={`chip ${period.all ? 'active' : ''}`} onClick={() => setPeriod((p) => ({ ...p, all: !p.all }))}>
            {t('complaints.all')}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="form-label">{t('complaints.groupby')}</span>
          {GROUPS.map((g) => (
            <button key={g} type="button" className={`chip ${group === g ? 'active' : ''}`} onClick={() => setGroup(g)}>
              {t(`complaints.group.${g}`)}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary btn-sm" disabled={!!exporting || loading} aria-busy={exporting === 'csv'}
            onClick={() => exportFile('csv')}>
            {exporting === 'csv'
              ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: 'inline-block', verticalAlign: '-2px', marginRight: 6 }} />{t('complaints.pdf.preparing')}</>
              : `↓ ${t('complaints.csv')}`}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={!!exporting || loading} aria-busy={exporting === 'pdf'}
            onClick={() => exportFile('pdf')}>
            {exporting === 'pdf'
              ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: 'inline-block', verticalAlign: '-2px', marginRight: 6 }} />{t('complaints.pdf.preparing')}</>
              : `↓ ${t('complaints.pdf')}`}
          </button>
        </div>
        <div className="form-hint" style={{ marginTop: 10 }}>{t('complaints.report.axis')}</div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>
      ) : error ? (
        <div className="error-banner">{error}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon="📊" title={t('complaints.report.empty')} message={t('complaints.report.empty.msg')} />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16, padding: 0 }}>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t(`complaints.group.${group}`)}</th>
                    <th className="num">{t('complaints.col.total')}</th>
                    <th className="num">{t('complaints.col.solved')}</th>
                    <th className="num">{t('complaints.col.pending')}</th>
                    <th className="num">{t('complaints.col.unfounded')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td style={{ fontWeight: 500 }}>{r.key || '–'}</td>
                      <td className="num">{r.total}</td>
                      <td className="num">{r.solved}</td>
                      <td className="num">
                        {r.pending > 0 ? <span className="badge badge-info">{r.pending}</span> : r.pending}
                      </td>
                      <td className="num">{r.unfounded}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>{t('complaints.col.all')}</td>
                    <td className="num" style={{ fontWeight: 700 }}>{totals.total}</td>
                    <td className="num" style={{ fontWeight: 700 }}>{totals.solved}</td>
                    <td className="num" style={{ fontWeight: 700 }}>{totals.pending}</td>
                    <td className="num" style={{ fontWeight: 700 }}>{totals.unfounded}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {totals.pending > 0 && <div className="form-hint" style={{ marginBottom: 16 }}>{t('complaints.report.pendingnote')}</div>}

          {lines.length > 0 && (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '16px 16px 0' }}><SectionHeader title={`${t('complaints.lines')} (${lines.length})`} /></div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('complaints.ordernumber')}</th>
                      <th>{t('complaints.model')}</th>
                      <th>{t('complaints.field.detail')}</th>
                      <th>{t('complaints.productiondate')}</th>
                      <th>{t('complaints.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={l.line_id || i} style={{ cursor: l.complaint_id ? 'pointer' : 'default' }}
                        onClick={() => l.complaint_id && navigate(`/complaints/${l.complaint_id}`)}>
                        <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-primary)' }}>{l.order_number}</td>
                        <td>{l.model || '–'}</td>
                        <td>{[mainComplaintLabel(taxonomy, l.main_complaint), l.detail].filter(Boolean).join(': ') || '–'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {l.production_date ? formatDay(l.production_date) : '–'}
                          {l.production_date_source === 'delivery' && (
                            <span className="badge badge-warning" style={{ fontSize: '0.625rem', marginLeft: 6 }}>{t('complaints.fallback')}</span>
                          )}
                        </td>
                        <td><span className={`badge ${STATUS_BADGE[l.status] || 'badge-secondary'}`}>{t(`complaints.status.${l.status}`, l.status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function SignalList({ t }) {
  const navigate = useNavigate();
  const [signals, setSignals] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/complaints/reports/signals')
      .then(({ data }) => setSignals(Array.isArray(data) ? data : []))
      .catch((err) => setError(getErrorMessage(err)));
  }, []);

  if (error) return <div className="error-banner">{error}</div>;
  if (!signals) return <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>;
  if (signals.length === 0) return <EmptyState icon="✓" title={t('complaints.signals.none')} message={t('complaints.signals.none.msg')} />;

  return (
    <>
      <div className="form-hint" style={{ marginBottom: 12 }}>{t('complaints.signals.hint')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {signals.map((s, i) => (
          <div key={`${s.model}-${s.detail}-${i}`} className="card" style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>{s.model || '–'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{[s.main_complaint, s.detail].filter(Boolean).join(': ')}</span>
              <div style={{ flex: 1 }} />
              <span className="badge badge-danger">
                {t('complaints.signals.count').replace('{count}', s.count).replace('{weeks}', s.weeks)}
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
              {t('complaints.productiondate')} {formatDay(s.first_production_date)} – {formatDay(s.last_production_date)}
            </div>
            {Array.isArray(s.complaint_ids) && s.complaint_ids.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {s.complaint_ids.map((cid, n) => (
                  <button key={cid} className="btn btn-secondary btn-sm" onClick={() => navigate(`/complaints/${cid}`)}>
                    {t('complaints.dossier')} {n + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function SignalSettings({ t }) {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/complaints/settings')
      .then(({ data }) => setForm({ signal_threshold: data?.signal_threshold ?? 3, signal_weeks: data?.signal_weeks ?? 6 }))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const threshold = parseInt(form.signal_threshold, 10);
    const weeks = parseInt(form.signal_weeks, 10);
    if (!(threshold >= 2) || !(weeks >= 1)) { toast.error(t('complaints.settings.invalid')); return; }
    setSaving(true);
    try {
      await api.put('/api/complaints/settings', { signal_threshold: threshold, signal_weeks: weeks });
      toast.success(t('complaints.settings.saved'));
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  if (!form) return <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>;

  return (
    <div className="card" style={{ maxWidth: 520 }}>
      <SectionHeader title={t('complaints.settings.title')} />
      <p className="form-hint" style={{ marginBottom: 16 }}>{t('complaints.settings.hint')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="form-group">
          <label className="form-label" htmlFor="sig-threshold">{t('complaints.settings.threshold')}</label>
          <input id="sig-threshold" type="number" min={2} className="input-field" value={form.signal_threshold}
            onChange={(e) => setForm((f) => ({ ...f, signal_threshold: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="sig-weeks">{t('complaints.settings.weeks')}</label>
          <input id="sig-weeks" type="number" min={1} className="input-field" value={form.signal_weeks}
            onChange={(e) => setForm((f) => ({ ...f, signal_weeks: e.target.value }))} />
        </div>
      </div>
      <div className="form-hint" style={{ marginBottom: 16 }}>
        {t('complaints.settings.preview').replace('{n}', form.signal_threshold || '?').replace('{w}', form.signal_weeks || '?')}
      </div>
      <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? t('complaints.saving') : t('common.save')}</button>
    </div>
  );
}
